
import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  RefreshCw, 
  FileText, 
  MessageCircle, 
  CheckCircle, 
  Users,
  Calendar,
  Headphones
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ContinuousGovernancePage: React.FC = () => {
  const faqItems = [
    {
      question: "Is this a replacement for counsel?",
      answer: "No. Governance support only. Use counsel for legal interpretation."
    },
    {
      question: "Can we do custom work under the subscription?",
      answer: "Light-touch support is included. Larger work is scoped as Advisory."
    }
  ];

  return (
    <Layout>
      <PageMeta
        title="BoardSight Governance Subscription | 12-Month AI Oversight Support"
        description="A 12-month subscription for boards: quarterly updates, template refreshes, briefings, and advisory access to keep AI governance current."
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/10 to-transparent">
        <div className="container-custom pt-16 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Governance Subscription (12 Months)
            </h1>
            <p className="text-2xl text-foreground/80 mb-4">
              Keep AI oversight current as expectations change.
            </p>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Ongoing updates, templates, quarterly briefings, and advisory access—so your board's AI governance doesn't drift as AI and regulation evolve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/contact#subscription">Request Subscription Details</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/contact#briefing">Book a Briefing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-background">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              What's Included
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Quarterly Updates */}
              <div className="bg-card rounded-xl p-8 shadow-lg border">
                <Calendar className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold mb-4">Quarterly Board-Ready Updates</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>What changed (governance expectations, enforcement patterns, major risk shifts)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>What it means for boards (actions + questions)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Suggested board agenda inserts and board-pack language</span>
                  </li>
                </ul>
              </div>

              {/* Template Refreshes */}
              <div className="bg-card rounded-xl p-8 shadow-lg border">
                <RefreshCw className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold mb-4">Template and Toolkit Refreshes</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Updated dashboards, reporting templates, checklists, and playbooks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>New modules as oversight needs evolve (e.g., third-party risk, agentic systems)</span>
                  </li>
                </ul>
              </div>

              {/* Advisory Access */}
              <div className="bg-card rounded-xl p-8 shadow-lg border">
                <Headphones className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold mb-4">Advisory Access (Light-Touch)</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Office hours/Q&A for directors and board-facing leaders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Quick reviews of board-pack language (within scope)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-16 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Users className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Who It's For
            </h2>
            <p className="text-lg text-muted-foreground">
              Best for boards that have completed the Intensive or established an initial governance model and want sustained support.
            </p>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-16 bg-background">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Annual Plans
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {/* Board Essentials */}
              <div className="bg-card rounded-xl p-8 shadow-lg border flex flex-col">
                <h3 className="text-xl font-semibold mb-2">Board Essentials</h3>
                <div className="text-3xl font-bold text-primary mb-4">
                  $4,000<span className="text-lg font-normal text-muted-foreground">/year</span>
                </div>
                <ul className="space-y-3 text-muted-foreground mb-8 flex-grow">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Quarterly updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Toolkit refreshes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>1 quarterly office hour</span>
                  </li>
                </ul>
              </div>

              {/* Board Plus */}
              <div className="bg-card rounded-xl p-8 shadow-lg border-2 border-primary flex flex-col relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                  Popular
                </div>
                <h3 className="text-xl font-semibold mb-2">Board Plus</h3>
                <div className="text-3xl font-bold text-primary mb-4">
                  $6,500<span className="text-lg font-normal text-muted-foreground">/year</span>
                </div>
                <ul className="space-y-3 text-muted-foreground mb-8 flex-grow">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Everything in Essentials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>2 quarterly office hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Priority Q&A</span>
                  </li>
                </ul>
              </div>

              {/* Board Premium */}
              <div className="bg-card rounded-xl p-8 shadow-lg border flex flex-col">
                <h3 className="text-xl font-semibold mb-2">Board Premium</h3>
                <div className="text-3xl font-bold text-primary mb-4">
                  $8,000<span className="text-lg font-normal text-muted-foreground">/year</span>
                </div>
                <ul className="space-y-3 text-muted-foreground mb-8 flex-grow">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Everything in Plus</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>4 quarterly office hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Annual oversight review session</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/contact#subscription">Request Subscription Details</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-medium">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Make AI oversight a standing capability—not a one-time project.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/contact#subscription">Request Details</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/contact#briefing">Book a Briefing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContinuousGovernancePage;
